import React, { useState, useEffect } from 'react';
import { inputCls, labelCls } from '../shared';

const ROOT_CAUSES = ['People Issues', 'Process Issues', 'Technology', 'External Dependencies', 'Resource Constraints', 'Communication'];
const IMPACT_LEVELS = ['Low', 'Medium', 'High', 'Critical'];
const STATUSES = ['Open', 'Investigating', 'Mitigating', 'Resolved'];

function impactColor(i) {
  if (i === 'Critical') return 'badge-danger';
  if (i === 'High') return 'badge-warning';
  if (i === 'Medium') return 'badge-primary';
  return 'badge-success';
}

function statusColor(s) {
  if (s === 'Resolved') return 'badge-success';
  if (s === 'Mitigating') return 'badge-primary';
  if (s === 'Investigating') return 'badge-warning';
  return 'badge-danger';
}

function rootCauseIcon(rc) {
  switch (rc) {
    case 'People Issues': return '👥';
    case 'Process Issues': return '⚙️';
    case 'Technology': return '💻';
    case 'External Dependencies': return '🔗';
    case 'Resource Constraints': return '📦';
    case 'Communication': return '💬';
    default: return '⚡';
  }
}

export default function BottlenecksPage({ counts = {}, setCounts }) {
  const [bottlenecks, setBottlenecks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('All');
  const [form, setForm] = useState({ title: '', rootCause: 'Process Issues', impact: 'Medium', status: 'Open', programme: '', description: '', owner: '' });

  useEffect(() => {
    if (setCounts) {
      setCounts(prev => ({ ...prev, bottlenecks: bottlenecks.length }));
    }
  }, [bottlenecks.length, setCounts]);

  const filtered = bottlenecks.filter(b => {
    if (filter === 'All') return true;
    if (STATUSES.includes(filter)) return b.status === filter;
    return b.rootCause === filter;
  });

  const openCount = bottlenecks.filter(b => b.status !== 'Resolved').length;

  const handleSubmit = e => {
    e.preventDefault();
    setBottlenecks(prev => [{ ...form, id: Date.now(), date: new Date().toISOString().split('T')[0] }, ...prev]);
    setForm({ title: '', rootCause: 'Process Issues', impact: 'Medium', status: 'Open', programme: '', description: '', owner: '' });
    setShowForm(false);
  };

  const handleDelete = id => {
    setBottlenecks(prev => prev.filter(b => b.id !== id));
  };

  const cycleStatus = id => {
    setBottlenecks(prev => prev.map(b => {
      if (b.id !== id) return b;
      const idx = STATUSES.indexOf(b.status);
      return { ...b, status: STATUSES[(idx + 1) % STATUSES.length] };
    }));
  };

  const rcSummary = ROOT_CAUSES.map(rc => ({
    label: rc,
    icon: rootCauseIcon(rc),
    count: bottlenecks.filter(b => b.rootCause === rc && b.status !== 'Resolved').length,
  }));

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="font-google text-[22px] font-semibold text-[var(--adani-text-primary)] leading-tight">Bottleneck Analysis</h1>
          <p className="text-[13px] text-[var(--adani-text-secondary)] mt-1">
            Identify and resolve project bottlenecks · <span className="font-medium text-[var(--adani-danger)]">{openCount} open</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select value={filter} onChange={e => setFilter(e.target.value)} className="topbar-filter">
            <option>All</option>
            <optgroup label="Status">{STATUSES.map(s => <option key={s}>{s}</option>)}</optgroup>
            <optgroup label="Root Cause">{ROOT_CAUSES.map(r => <option key={r}>{r}</option>)}</optgroup>
          </select>
          <button onClick={() => setShowForm(!showForm)} className="btn-primary text-[13px]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Report Bottleneck
          </button>
        </div>
      </div>

      {/* Root Cause Summary Bar */}
      <div className="glass-card-static rounded-2xl p-4 mb-5">
        <div className="flex items-center gap-6 overflow-x-auto">
          <span className="text-[12px] font-semibold text-[var(--adani-text-tertiary)] uppercase tracking-wide shrink-0">Root Causes</span>
          {rcSummary.map(rc => (
            <div key={rc.label} className="flex items-center gap-2 shrink-0">
              <span className="text-sm">{rc.icon}</span>
              <span className="text-[12px] text-[var(--adani-text-secondary)]">{rc.label}</span>
              <span className={`text-[12px] font-semibold ${rc.count > 0 ? 'text-[var(--adani-danger)]' : 'text-[var(--adani-text-tertiary)]'}`}>{rc.count}</span>
            </div>
          ))}
        </div>
      </div>

      {showForm && (
        <div className="glass-card-static rounded-2xl p-5 mb-5 animate-slide-up">
          <h3 className="text-[15px] font-semibold text-[var(--adani-text-primary)] mb-4">Report Bottleneck</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Title</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="Brief description of the bottleneck" required />
              </div>
              <div>
                <label className={labelCls}>Programme</label>
                <input value={form.programme} onChange={e => setForm(f => ({ ...f, programme: e.target.value }))} className={inputCls} placeholder="e.g. Adani Defence" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Root Cause</label>
                <select value={form.rootCause} onChange={e => setForm(f => ({ ...f, rootCause: e.target.value }))} className={inputCls + ' cursor-pointer'}>
                  {ROOT_CAUSES.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Impact</label>
                <select value={form.impact} onChange={e => setForm(f => ({ ...f, impact: e.target.value }))} className={inputCls + ' cursor-pointer'}>
                  {IMPACT_LEVELS.map(i => <option key={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Owner</label>
                <input value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} className={inputCls} placeholder="Person responsible" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={inputCls} rows={3} placeholder="What's blocking progress and what's the impact…" required />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-[13px]">Cancel</button>
              <button type="submit" className="btn-primary text-[13px]">Submit</button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map(b => (
          <div key={b.id} className="glass-card rounded-2xl px-5 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3.5 min-w-0 flex-1">
                <span className="text-lg mt-0.5 shrink-0">{rootCauseIcon(b.rootCause)}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[14px] font-medium text-[var(--adani-text-primary)]">{b.title}</p>
                    <span className={`badge ${impactColor(b.impact)}`}>{b.impact}</span>
                    <button onClick={() => cycleStatus(b.id)} className={`badge ${statusColor(b.status)} cursor-pointer hover:opacity-80 transition-opacity`}>
                      {b.status}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {b.programme && <span className="text-[12px] text-[var(--adani-primary)] font-medium">{b.programme}</span>}
                    {b.programme && <span className="text-[var(--adani-text-tertiary)]">·</span>}
                    <span className="text-[12px] text-[var(--adani-text-tertiary)]">{b.rootCause}</span>
                  </div>
                  <p className="text-[13px] text-[var(--adani-text-secondary)] mt-2 leading-relaxed">{b.description}</p>
                  <div className="flex items-center gap-3 mt-2.5">
                    <span className="text-[11px] text-[var(--adani-text-tertiary)]">{b.date}</span>
                    {b.owner && (
                      <>
                        <span className="text-[11px] text-[var(--adani-text-tertiary)]">·</span>
                        <span className="text-[11px] text-[var(--adani-text-tertiary)]">{b.owner}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button onClick={() => handleDelete(b.id)} className="btn-icon shrink-0 text-[var(--adani-text-tertiary)] hover:text-[var(--adani-danger)]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="glass-card-static rounded-2xl">
            <div className="empty-state">
              <div className="empty-state-icon"><span className="text-2xl">⚡</span></div>
              <p className="empty-state-title">No bottlenecks found</p>
              <p className="empty-state-text">Click "Report Bottleneck" to flag an issue</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
